import { Navigate, useRoutes } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { LinearProgress } from '@mui/material';
import DashboardLayout from './layouts/dashboard';
import LogoOnlyLayout from './layouts/LogoOnlyLayout';
import Login from './pages/Login';
import Register from './pages/Register';
import DashboardApp from './pages/DashboardApp';
import User from './pages/User';
import AddUser from './pages/AddUser';
import EditUser from './pages/EditUser';
import Device from './pages/Device';
import EditDevice from './pages/EditDevice';
import AddDevice from './pages/AddDevice';
import NotFound from './pages/Page404';
import MyClub from './pages/MyClub';
import MyClubEdit from './pages/MyClubEdit';
import Club from './pages/Club';
import AddClub from './pages/AddClub';
import EditClub from './pages/EditClub';
import LiveMap from './pages/LiveMap';
import FlightList from './pages/FlightList';
import ProfileUser from './pages/Profile';
import SettingsProfile from './pages/SettingsProfile';
import FlightListAssign from './pages/FlightListAssign';
import FlightReview from './pages/FlightReview';
import VolShare from './pages/VolShare';
import VolShareLayout from './layouts/volShare';
import Subscription from './pages/Subscription';
import Payment from './pages/Payment';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ReportClub from './pages/ReportClub';
import Geofences from './pages/Geofences';
import LiveReport from './pages/LiveReport';
import AerosDetails from './pages/AerosDetails';
import AddAero from './pages/AddAero';
import EditAero from './pages/EditAero';
import Aeros from './pages/Aeros';
import AddRunways from './pages/AddRunways';
import EditRunways from './pages/EditRunways';

import {
  selectAuthenticatedUser,
  selectSession,
  selectGroupSelectedId,
  selectAbonnementData
} from './createSelectorUtils';
// ----------------------------------------------------------------------

export default function Router() {
  const session = useSelector(selectSession);
  const authenticated = useSelector(selectAuthenticatedUser);

  const groupId = useSelector(selectGroupSelectedId);
  const permission = session.permission
    ? session.permission.find((item) => item.groupid === groupId) || {}
    : {};
  const abonnement = useSelector(selectAbonnementData);

  const isLogin = authenticated && groupId !== -1 && session.length !== 0 && abonnement !== null;

  return useRoutes([
    {
      path: 'dashboard',
      element: isLogin ? <DashboardLayout /> : <LinearProgress />,
      children: [{ path: '', element: <DashboardApp /> }]
    },
    {
      path: 'profile',
      element: isLogin ? <DashboardLayout /> : <LinearProgress />,
      children: [
        { path: '', element: <ProfileUser /> },
        { path: 'settings', element: <SettingsProfile /> }
      ]
    },
    {
      path: 'live/map',
      element: isLogin ? <DashboardLayout /> : <LinearProgress />,
      children: [
        {
          path: '',
          element:
            !session.administrator && !(permission.status < 3) && groupId !== 0 ? (
              <Navigate to="/dashboard" />
            ) : (
              <LiveMap />
            )
        }
      ]
    },
    {
      path: 'geofences',
      element: isLogin ? <DashboardLayout /> : <LinearProgress />,
      children: [
        {
          path: '',
          element: !session.administrator ? <Navigate to="/dashboard" /> : <Geofences />
        }
      ]
    },

    {
      path: 'aeros',
      element: isLogin ? <DashboardLayout /> : <LinearProgress />,
      children: [
        {
          path: '',
          element: abonnement ? <Aeros /> : <Navigate to="/subscription" />
        },
        {
          path: 'details',
          element: abonnement ? <AerosDetails /> : <Navigate to="/subscription" />
        },
        {
          path: 'add/runways',
          element: !session.administrator ? <Navigate to="/dashboard" /> : <AddRunways />
        },
        {
          path: 'edit/runways',
          element: !session.administrator ? <Navigate to="/dashboard" /> : <EditRunways />
        },
        {
          path: 'add/aeros',
          element: <AddAero />
        },
        {
          path: 'edit/aeros',
          element: !session.administrator ? <Navigate to="/dashboard" /> : <EditAero />
        }
      ]
    },
    {
      path: 'live/report',
      element: isLogin ? <DashboardLayout /> : <LinearProgress />,
      children: [
        {
          path: '',
          element: !session.administrator ? <Navigate to="/dashboard" /> : <LiveReport />
        }
      ]
    },
    {
      path: 'flight',
      element: isLogin ? <DashboardLayout /> : <LinearProgress />,
      children: [
        { path: '', element: <Navigate to="/flight/list" /> },
        { path: 'list', element: <FlightList /> },
        { path: 'assign', element: <FlightListAssign /> },
        { path: 'review', element: <FlightReview /> }
      ]
    },
    {
      path: 'user',
      element: isLogin ? <DashboardLayout /> : <LinearProgress />,
      children: [
        {
          path: '',
          element:
            !session.administrator && !(permission.status < 3) ? (
              <Navigate to="/dashboard" />
            ) : (
              <User />
            )
        },
        {
          path: 'add',
          element:
            !session.administrator && !(permission.status < 3) ? (
              <Navigate to="/dashboard" />
            ) : (
              <AddUser />
            )
        },
        {
          path: 'edit',
          element:
            !session.administrator && !(permission.status < 3) ? (
              <Navigate to="/dashboard" />
            ) : (
              <EditUser />
            )
        }
      ]
    },
    {
      path: 'device',
      element: isLogin ? <DashboardLayout /> : <LinearProgress />,
      children: [
        {
          path: '',
          element:
            session.administrator || permission.status < 3 || groupId === 0 ? (
              <Device />
            ) : (
              <Navigate to="/dashboard" />
            )
        },
        {
          path: 'add',
          element: !(session.administrator || groupId === 0) ? (
            <Navigate to="/dashboard" />
          ) : (
            <AddDevice />
          )
        },
        {
          path: 'edit',
          element:
            !(session.administrator || groupId === 0) && !(permission.status < 3) ? (
              <Navigate to="/dashboard" />
            ) : (
              <EditDevice />
            )
        }
      ]
    },
    {
      path: 'report',
      element: isLogin ? <DashboardLayout /> : <LinearProgress />,
      children: [
        {
          path: 'club',
          element:
            !session.administrator && !(permission.status < 3) ? (
              <Navigate to="/dashboard" />
            ) : (
              <ReportClub />
            )
        }
      ]
    },
    {
      path: 'myclub',
      element: isLogin ? <DashboardLayout /> : <LinearProgress />,
      children: [
        { path: '', element: <MyClub /> },
        { path: 'edit', element: <MyClubEdit /> }
      ]
    },
    {
      path: 'club',
      element: isLogin ? <DashboardLayout /> : <LinearProgress />,
      children: [
        { path: '', element: !session.administrator ? <Navigate to="/dashboard" /> : <Club /> },
        {
          path: 'add',
          element: !session.administrator ? <Navigate to="/dashboard" /> : <AddClub />
        },
        {
          path: 'edit',
          element: !session.administrator ? <Navigate to="/dashboard" /> : <EditClub />
        }
      ]
    },
    {
      path: 'subscription',
      element: isLogin ? <DashboardLayout /> : <LinearProgress />,
      children: [
        {
          path: '',
          element:
            permission.status === 3 || permission.status === 4 || groupId === 0 ? (
              <Subscription />
            ) : (
              <Navigate to="/dashboard" />
            )
        },
        {
          path: 'plans',
          element:
            permission.status === 3 || permission.status === 4 || groupId === 0 ? (
              <Subscription />
            ) : (
              <Navigate to="/dashboard" />
            )
        },
        {
          path: 'payment',
          element:
            permission.status === 3 || permission.status === 4 || groupId === 0 ? (
              <Payment />
            ) : (
              <Navigate to="/dashboard" />
            )
        }
      ]
    },
    {
      path: 'vol/share',
      element: <VolShareLayout />,
      children: [{ path: '', element: <VolShare /> }]
    },
    {
      path: '/',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'login', element: <Login /> },
        { path: 'register', element: <Register /> },
        { path: 'forgot/password', element: <ForgotPassword /> },
        { path: 'reset/password', element: <ResetPassword /> },
        { path: 'first/connection/reset', element: <ResetPassword /> },
        { path: '404', element: <NotFound /> },
        { path: '/', element: <Navigate to="/dashboard" /> },
        { path: '*', element: <Navigate to="/404" /> }
      ]
    }
  ]);
}
